import { ZONE_IDS, ZoneId, zoneIdForTarget } from '../../helpers/goalTargetFrame';
import { POOL_ZONE_IDS, PoolZoneId, poolZoneIdForLocation } from '../../helpers/poolShotZones';
import { EventLightVm, MatchPlayerVm } from '../../utils/api';
import {
    cellsToZoneCounts,
    GK_SITUATION_LABELS,
    GkSituation,
    GkSituationKey,
    matchesGkSituation,
    matchesSelectedSituations,
    ZoneCounts,
} from './insightsGoalkeeperZones';
import {
    buildScoreAtMomentByEventId,
    emptyMapSide,
    MapOutcome,
    mapPointContext,
    MapSide,
    matchFinalScoreLine,
} from './insightsMapData';
import { MapPeriodKey, matchesPeriodFilter, nameByMatchPlayerId } from './insightsMatchStats';
import { MatchBundle } from './insightsQueries';
import { matchesShotKind, ShotKindFilter } from './insightsShotKindFilters';

/*
 * Field-player shot maps: the player's own shots for our club (takenById),
 * same situation splits as GK maps. Outcomes are shooter-centric (goal vs not).
 */

export const PLAYER_SHOT_SAMPLE_WARNING = 8;

export interface ShooterZoneCell {
    zone: ZoneId;
    shots: number;
    goals: number;
    /** Missed + saved + blocked. */
    misses: number;
}

export interface ShooterPoolZoneCell {
    zone: PoolZoneId;
    shots: number;
    goals: number;
    misses: number;
}

export interface ShooterZoneStats {
    cells: ShooterZoneCell[];
    poolCells: ShooterPoolZoneCell[];
    shots: number;
    goals: number;
    misses: number;
    shootPct?: number;
    sampleWarning: boolean;
    located: number;
    total: number;
}

export interface ShooterSituationView {
    situation: GkSituation;
    label: string;
    stats: ShooterZoneStats;
    goalMap: MapSide;
    poolMap: MapSide;
}

const emptyGoalCell = (zone: ZoneId): ShooterZoneCell => ({
    zone,
    shots: 0,
    goals: 0,
    misses: 0,
});

const emptyPoolCell = (zone: PoolZoneId): ShooterPoolZoneCell => ({
    zone,
    shots: 0,
    goals: 0,
    misses: 0,
});

const emptyGoalCells = (): ShooterZoneCell[] => ZONE_IDS.map(emptyGoalCell);
const emptyPoolCells = (): ShooterPoolZoneCell[] => POOL_ZONE_IDS.map(emptyPoolCell);

const bumpGoal = (cells: ShooterZoneCell[], zone: ZoneId, isGoal: boolean): void => {
    const cell = cells.find((c) => c.zone === zone);
    if (!cell) return;
    cell.shots += 1;
    if (isGoal) cell.goals += 1;
    else cell.misses += 1;
};

const bumpPool = (cells: ShooterPoolZoneCell[], zone: PoolZoneId, isGoal: boolean): void => {
    const cell = cells.find((c) => c.zone === zone);
    if (!cell) return;
    cell.shots += 1;
    if (isGoal) cell.goals += 1;
    else cell.misses += 1;
};

const shotOutcome = (event: EventLightVm): MapOutcome => {
    const shot = event.shot;
    if (!shot) return 'missed';
    if (shot.isGoal) return 'goal';
    if (shot.savedById != null) return 'saved';
    if (shot.blockedById != null) return 'blocked';
    return 'missed';
};

const indexLineup = (bundle: MatchBundle): Map<number, { playerId: number; teamId: number }> => {
    const byMatchPlayerId = new Map<number, { playerId: number; teamId: number }>();
    (bundle.detail.players || []).forEach((mp: MatchPlayerVm) => {
        if (mp.id == null || mp.playerId == null || mp.teamId == null) return;
        byMatchPlayerId.set(mp.id, { playerId: mp.playerId, teamId: mp.teamId });
    });
    return byMatchPlayerId;
};

type PlayerShot = {
    event: EventLightVm;
    bundleId: number;
};

const shotsByPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    situation?: GkSituation | GkSituationKey[],
    periodKeys?: MapPeriodKey[] | null,
    kind?: ShotKindFilter
): PlayerShot[] => {
    const found: PlayerShot[] = [];

    bundles.forEach((bundle) => {
        const lineup = indexLineup(bundle);
        bundle.events.forEach((event: EventLightVm) => {
            if (!event.shot) return;
            if (!matchesPeriodFilter(event, periodKeys)) return;
            if (!ourTeamIds.includes(event.shot.teamId)) return;
            if (Array.isArray(situation)) {
                if (!matchesSelectedSituations(event.shot, situation)) return;
            } else if (!matchesGkSituation(event.shot, situation)) {
                return;
            }
            if (!matchesShotKind(event.shot, kind)) return;

            const shooter = lineup.get(event.shot.takenById);
            if (!shooter || shooter.playerId !== playerId) return;

            found.push({ event, bundleId: bundle.detail.id });
        });
    });

    return found;
};

export const countPlayerShots = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number
): number => shotsByPlayer(bundles, ourTeamIds, playerId).length;

export const collectShooterZoneStats = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    situation?: GkSituation | GkSituationKey[],
    periodKeys?: MapPeriodKey[] | null,
    kind?: ShotKindFilter
): ShooterZoneStats => {
    const cells = emptyGoalCells();
    const poolCells = emptyPoolCells();
    let shots = 0;
    let goals = 0;
    let misses = 0;
    let located = 0;
    let total = 0;

    shotsByPlayer(bundles, ourTeamIds, playerId, situation, periodKeys, kind).forEach((item) => {
        const shot = item.event.shot!;
        const isGoal = !!shot.isGoal;
        total += 1;
        shots += 1;
        if (isGoal) goals += 1;
        else misses += 1;

        const goalZone = zoneIdForTarget(shot.targetX, shot.targetY);
        if (goalZone != null && goalZone !== 'offTarget') {
            located += 1;
            bumpGoal(cells, goalZone, isGoal);
        }

        const poolZone = poolZoneIdForLocation(shot.locationX, shot.locationY);
        if (poolZone != null && poolZone !== 'deep') {
            bumpPool(poolCells, poolZone, isGoal);
        }
    });

    return {
        cells,
        poolCells,
        shots,
        goals,
        misses,
        shootPct: shots > 0 ? Math.round((goals / shots) * 100) : undefined,
        sampleWarning: shots > 0 && shots < PLAYER_SHOT_SAMPLE_WARNING,
        located,
        total,
    };
};

/** ZoneCounts for map overlay: `saves` holds misses (shooter mode labels). */
export const shooterCellsToZoneCounts = (
    cells: Array<{ zone: string; shots: number; goals: number; misses: number }>
): ZoneCounts =>
    cellsToZoneCounts(
        cells.map((cell) => ({
            zone: cell.zone,
            shots: cell.shots,
            goals: cell.goals,
            saves: cell.misses,
        }))
    );

export const shootPctText = (goals: number, shots: number): string =>
    shots > 0 ? `${Math.round((goals / shots) * 100)}%` : '-';

export const cellShootPctText = (goals: number, shots: number): string =>
    shots > 0 ? `${Math.round((goals / shots) * 100)}%` : '-';

export const mapTargetsByPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    situation?: GkSituation | GkSituationKey[],
    periodKeys?: MapPeriodKey[] | null,
    kind?: ShotKindFilter
): MapSide => {
    const side = emptyMapSide();

    bundles.forEach((bundle) => {
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        shotsByPlayer([bundle], ourTeamIds, playerId, situation, periodKeys, kind).forEach((item) => {
            const shot = item.event.shot!;
            side.total += 1;
            const x = shot.targetX;
            const y = shot.targetY;
            if (x == null || y == null || x < 0 || x > 1 || y < 0 || y > 1) return;
            const outcome = shotOutcome(item.event);
            const resultLine =
                outcome === 'goal'
                    ? 'Goal'
                    : outcome === 'saved'
                    ? 'Saved'
                    : outcome === 'blocked'
                    ? 'Blocked'
                    : 'Miss';
            const ctx = mapPointContext(
                bundle.detail,
                item.event,
                resultLine,
                names,
                scoreAt.get(item.event.id),
                finalScore
            );
            side.points.push({
                key: `${item.bundleId}-${item.event.id}-t`,
                x,
                y,
                outcome,
                ...ctx,
            });
        });
    });

    return side;
};

export const mapLocationsByPlayer = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number,
    situation?: GkSituation | GkSituationKey[],
    periodKeys?: MapPeriodKey[] | null,
    kind?: ShotKindFilter
): MapSide => {
    const side = emptyMapSide();

    bundles.forEach((bundle) => {
        const names = nameByMatchPlayerId(bundle.detail);
        const scoreAt = buildScoreAtMomentByEventId(bundle.detail, bundle.events);
        const finalScore = matchFinalScoreLine(bundle.detail, bundle.events);
        shotsByPlayer([bundle], ourTeamIds, playerId, situation, periodKeys, kind).forEach((item) => {
            const shot = item.event.shot!;
            side.total += 1;
            const x = shot.locationX;
            const y = shot.locationY;
            if (x == null || y == null || x < 0 || x > 1 || y < 0 || y > 1) return;
            const outcome = shotOutcome(item.event);
            const resultLine =
                outcome === 'goal'
                    ? 'Goal'
                    : outcome === 'saved'
                    ? 'Saved'
                    : outcome === 'blocked'
                    ? 'Blocked'
                    : 'Miss';
            const ctx = mapPointContext(
                bundle.detail,
                item.event,
                resultLine,
                names,
                scoreAt.get(item.event.id),
                finalScore
            );
            side.points.push({
                key: `${item.bundleId}-${item.event.id}-l`,
                x,
                y,
                outcome,
                ...ctx,
            });
        });
    });

    return side;
};

export const buildShooterSituationViews = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number
): ShooterSituationView[] => {
    const order: GkSituation[] = ['extraPlayer', 'sixOnSix', 'penalty', 'all'];
    return order.map((situation) => ({
        situation,
        label: GK_SITUATION_LABELS[situation],
        stats: collectShooterZoneStats(bundles, ourTeamIds, playerId, situation),
        goalMap: mapTargetsByPlayer(bundles, ourTeamIds, playerId, situation),
        poolMap: mapLocationsByPlayer(bundles, ourTeamIds, playerId, situation),
    }));
};

/**
 * GK maps when attributed SOG exists; shooter maps when they have own shots.
 * Both can show when a player has both (rare).
 */
export const resolvePlayerMapRole = (input: {
    gkSog: number;
    fieldShots: number;
}): { showGk: boolean; showShooter: boolean } => ({
    showGk: input.gkSog > 0,
    showShooter: input.fieldShots > 0,
});
